import { useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import type { SessionUser } from '../../api/types.js';
import {
  Alert,
  Field,
  Notice,
  StatusPill,
  datum,
  fehlertext,
  pct,
  useLoad,
} from '../shared/ui.js';

type Dimension = 'PRODUCT_TYPE' | 'MATERIAL' | 'PRODUCT_MATERIAL' | 'ARTICLE';

interface Deviation {
  id: string;
  dimension: Dimension;
  discountPercent: string;
  note: string | null;
  product: {
    id: string;
    shortName: string;
    typeValue: { code: string };
    standardValue: { label: { de?: string } } | null;
  } | null;
  materialValue: { id: string; code: string; label: { de?: string } } | null;
  /** ADR-0023. */
  article: { id: string; articleNumber: string; description: string } | null;
}
interface Version {
  id: string;
  validFrom: string;
  validTo: string | null;
  baseDiscountPercent: string;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: { displayName: string };
  updatedBy: { displayName: string } | null;
  deviations: Deviation[];
  _count: { quotes: number };
}

const heute = () => new Date().toISOString().slice(0, 10);
const gueltig = (v: Version) =>
  v.validFrom.slice(0, 10) <= heute() &&
  (v.validTo === null || v.validTo.slice(0, 10) >= heute());

/**
 * Konditionen eines Kunden: aktuelle Kondition, neue Version, Historie.
 *
 * Rollengrenze sichtbar und serverseitig: Grundrabatt und Versionen fuer Vertrieb
 * und Betreiber, Abweichungen nur fuer den Betreiber.
 */
export function ConditionsTab({
  accountId,
  user,
}: {
  accountId: string;
  user: SessionUser;
}): React.JSX.Element {
  const { data, error, reload } = useLoad<{ items: Version[] }>(
    `/api/admin/accounts/${accountId}/conditions`,
  );
  const admin = user.role === 'operator_admin';
  const versionen = data?.items ?? [];
  const aktuelle = versionen.find(gueltig) ?? null;
  const [neuOffen, setNeuOffen] = useState(false);

  return (
    <>
      <Alert text={error} />
      <section className="card">
        <h3>Aktuell gueltige Kondition</h3>
        {aktuelle === null ? (
          <Notice>
            <strong>Kondition noch nicht eingerichtet.</strong>
            <p className="note">
              Ohne gueltige Kondition erhaelt dieser Kunde keine Preise und kein Angebot.
              Es wird kein Nullrabatt angenommen.
            </p>
          </Notice>
        ) : (
          <KonditionAnzeige v={aktuelle} />
        )}
        <div className="row">
          <button type="button" className="primary" onClick={() => setNeuOffen(true)}>
            Neue Konditionsversion
          </button>
        </div>
      </section>

      {neuOffen && (
        <NeueVersion
          accountId={accountId}
          vorlagen={versionen}
          onDone={() => {
            setNeuOffen(false);
            reload();
          }}
          onCancel={() => setNeuOffen(false)}
        />
      )}

      <section className="card">
        <h3>Wie der Rabatt bestimmt wird</h3>
        <p className="note">
          Fuer jeden Artikel gilt genau <strong>ein</strong> Rabattsatz: der{' '}
          <strong>niedrigste</strong> Wert unter allen zutreffenden Regeln einschliesslich
          des Grundrabatts. Produkttyp-, Werkstoff-, Kombinations- und
          Einzelartikelabweichung sind gleichwertige Kandidaten -- keine Regel gewinnt
          automatisch, nur weil sie spezifischer ist. Rabatte werden nie addiert. Globale
          Eingriffe des Betreibers koennen den ermittelten Satz weiter senken, nie
          erhoehen. Hoechstens 40 %.
        </p>
      </section>

      <section className="card">
        <h3>Historie</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Gueltig ab</th>
                <th>Gueltig bis</th>
                <th className="num">Grundrabatt</th>
                <th className="num">Abweichungen</th>
                <th>Status</th>
                <th>Angelegt</th>
                <th>Geaendert</th>
                <th className="num">Angebote</th>
              </tr>
            </thead>
            <tbody>
              {versionen.map((v) => (
                <tr key={v.id}>
                  <td>{datum(v.validFrom)}</td>
                  <td>
                    {v.validTo ? datum(v.validTo) : <span className="muted">offen</span>}
                  </td>
                  <td className="num">{pct(v.baseDiscountPercent)}</td>
                  <td className="num">{v.deviations.length}</td>
                  <td>
                    {gueltig(v) ? (
                      <StatusPill active activeText="gueltig" />
                    ) : v.validFrom.slice(0, 10) > heute() ? (
                      <span className="pill">kuenftig</span>
                    ) : (
                      <span className="pill pill-off">abgelaufen</span>
                    )}
                  </td>
                  <td>
                    {v.createdBy.displayName}
                    <br />
                    <span className="note">{datum(v.createdAt)}</span>
                  </td>
                  <td>
                    {v.updatedBy ? (
                      <>
                        {v.updatedBy.displayName}
                        <br />
                        <span className="note">{datum(v.updatedAt)}</span>
                      </>
                    ) : (
                      '–'
                    )}
                  </td>
                  <td className="num">{v._count.quotes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {admin && <AusnahmenVonGlobalenRegeln accountId={accountId} />}

      {versionen.map((v) => (
        <VersionDetail
          key={v.id}
          v={v}
          admin={admin}
          aktuell={v.id === aktuelle?.id}
          onChange={reload}
        />
      ))}
    </>
  );
}

function KonditionAnzeige({ v }: { v: Version }): React.JSX.Element {
  return (
    <dl className="kopfdaten wide">
      <dt>Grundrabatt</dt>
      <dd>
        <strong>{pct(v.baseDiscountPercent)}</strong>
      </dd>
      <dt>Gueltig</dt>
      <dd>
        ab {datum(v.validFrom)}
        {v.validTo ? ` bis ${datum(v.validTo)}` : ', unbefristet'}
      </dd>
      <dt>Abweichungen</dt>
      <dd>
        {v.deviations.length === 0 ? (
          <span className="muted">keine</span>
        ) : (
          <ul className="plain">
            {v.deviations.map((d) => (
              <li key={d.id}>
                {abweichungText(d)} → <strong>{pct(d.discountPercent)}</strong>
              </li>
            ))}
          </ul>
        )}
      </dd>
    </dl>
  );
}

function produktText(p: NonNullable<Deviation['product']>): string {
  return `${p.typeValue.code} – ${p.shortName}${p.standardValue?.label?.de ? ` ${p.standardValue.label.de}` : ''}`;
}

function werkstoffText(m: NonNullable<Deviation['materialValue']>): string {
  return `${m.code} – ${m.label?.de ?? ''}`;
}

function abweichungText(d: Deviation): string {
  switch (d.dimension) {
    case 'PRODUCT_TYPE':
      return d.product ? `Produkt ${produktText(d.product)}` : d.dimension;
    case 'MATERIAL':
      return d.materialValue
        ? `Werkstoff ${werkstoffText(d.materialValue)}`
        : d.dimension;
    case 'PRODUCT_MATERIAL':
      return d.product && d.materialValue
        ? `Produkt ${produktText(d.product)} + Werkstoff ${werkstoffText(d.materialValue)}`
        : d.dimension;
    case 'ARTICLE':
      return d.article
        ? `Artikel ${d.article.articleNumber} – ${d.article.description}`
        : d.dimension;
  }
}

function NeueVersion({
  accountId,
  vorlagen,
  onDone,
  onCancel,
}: {
  accountId: string;
  vorlagen: Version[];
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [modus, setModus] = useState<'kopie' | 'leer'>(
    vorlagen.length > 0 ? 'kopie' : 'leer',
  );
  const [quelle, setQuelle] = useState(vorlagen[0]?.id ?? '');
  const [validFrom, setValidFrom] = useState(heute());
  const [basis, setBasis] = useState(vorlagen[0]?.baseDiscountPercent ?? '');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    try {
      await api.post(`/api/admin/accounts/${accountId}/conditions`, {
        validFrom,
        baseDiscountPercent: Number(basis),
        note: note || undefined,
        ...(modus === 'kopie' && quelle ? { copyFromVersionId: quelle } : {}),
      });
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <section className="card highlight">
      <h3>Neue Konditionsversion</h3>
      <form onSubmit={speichern}>
        <div className="row">
          <label>
            <input
              type="radio"
              checked={modus === 'kopie'}
              disabled={vorlagen.length === 0}
              onChange={() => setModus('kopie')}
            />{' '}
            Vorherige Kondition uebernehmen
          </label>
          <label>
            <input
              type="radio"
              checked={modus === 'leer'}
              onChange={() => setModus('leer')}
            />{' '}
            Leer anlegen
          </label>
        </div>
        {modus === 'kopie' && (
          <p className="note">
            Kopiert Grundrabatt und Abweichungen <strong>einmalig</strong>. Die neue
            Version ist danach eigenstaendig; eine spaetere Aenderung der Vorlage
            veraendert sie nicht.
          </p>
        )}
        <div className="grid2">
          {modus === 'kopie' && (
            <Field label="Vorlage">
              <select
                value={quelle}
                onChange={(e) => {
                  setQuelle(e.target.value);
                  const v = vorlagen.find((x) => x.id === e.target.value);
                  if (v) setBasis(v.baseDiscountPercent);
                }}
              >
                {vorlagen.map((v) => (
                  <option key={v.id} value={v.id}>
                    ab {datum(v.validFrom)} · {pct(v.baseDiscountPercent)} ·{' '}
                    {v.deviations.length} Abw.
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field
            label="Gueltig ab"
            hint="Die bisher offene Version endet automatisch am Vortag."
          >
            <input
              type="date"
              required
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
            />
          </Field>
          <Field label="Grundrabatt in %">
            <input
              type="number"
              required
              min={0}
              max={40}
              step="0.5"
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
            />
          </Field>
          <Field label="Notiz">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
          </Field>
        </div>
        <Alert text={err} />
        <div className="row">
          <button type="submit" className="primary">
            Version anlegen
          </button>
          <button type="button" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}

function VersionDetail({
  v,
  admin,
  aktuell,
  onChange,
}: {
  v: Version;
  admin: boolean;
  aktuell: boolean;
  onChange: () => void;
}): React.JSX.Element {
  const [offen, setOffen] = useState(aktuell);
  const gesperrt = v._count.quotes > 0;
  const [basis, setBasis] = useState(v.baseDiscountPercent);
  const [err, setErr] = useState<string | null>(null);
  const produkte = useLoad<{
    items: Array<{
      id: string;
      shortName: string;
      typeValue: { code: string };
      standardValue: { label: { de?: string } } | null;
    }>;
  }>(admin && offen ? '/api/admin/master/products' : null);
  const werkstoffe = useLoad<{
    items: Array<{ id: string; code: string; label: { de?: string }; isActive: boolean }>;
  }>(admin && offen ? '/api/admin/master/material' : null);
  const [abw, setAbw] = useState({
    dimension: 'PRODUCT_TYPE' as Dimension,
    produktRef: '',
    werkstoffRef: '',
    artikelId: '',
    prozent: '',
  });
  // Erzwingt einen Neuaufbau der Artikelsuche nach jedem Speichern, damit ihr
  // interner Suchtext nicht die naechste Eingabe ueberlagert.
  const [artikelSucheKey, setArtikelSucheKey] = useState(0);

  async function grundrabatt(): Promise<void> {
    setErr(null);
    try {
      await api.patch(`/api/admin/accounts/conditions/${v.id}`, {
        baseDiscountPercent: Number(basis),
      });
      onChange();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function abweichung(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    try {
      await api.post(`/api/admin/accounts/conditions/${v.id}/deviations`, {
        dimension: abw.dimension,
        discountPercent: Number(abw.prozent),
        ...(abw.dimension === 'PRODUCT_TYPE'
          ? { productId: abw.produktRef }
          : abw.dimension === 'MATERIAL'
            ? { materialValueId: abw.werkstoffRef }
            : abw.dimension === 'PRODUCT_MATERIAL'
              ? { productId: abw.produktRef, materialValueId: abw.werkstoffRef }
              : { articleId: abw.artikelId }),
      });
      setAbw({ ...abw, produktRef: '', werkstoffRef: '', artikelId: '', prozent: '' });
      setArtikelSucheKey((k) => k + 1);
      onChange();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function entfernen(id: string): Promise<void> {
    try {
      await api.post(`/api/admin/accounts/deviations/${id}/remove`);
      onChange();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <section className="card">
      <h3>
        <button type="button" className="link" onClick={() => setOffen(!offen)}>
          {offen ? '▾' : '▸'} Version ab {datum(v.validFrom)}
        </button>
        {aktuell && (
          <span className="pill pill-ok" style={{ marginLeft: '0.5rem' }}>
            gueltig
          </span>
        )}
        {gesperrt && (
          <span className="pill" style={{ marginLeft: '0.5rem' }}>
            unveraenderlich, {v._count.quotes} Angebote
          </span>
        )}
      </h3>
      {offen && (
        <>
          <Alert text={err} />
          {gesperrt && (
            <p className="note">
              Auf diese Version verweisen ausgestellte Angebote. Sie ist unveraenderlich;
              Aenderungen erfolgen ueber eine neue Version.
            </p>
          )}
          <div className="grid2">
            <Field label="Grundrabatt in %">
              <div className="row">
                <input
                  type="number"
                  min={0}
                  max={40}
                  step="0.5"
                  value={basis}
                  disabled={gesperrt}
                  onChange={(e) => setBasis(e.target.value)}
                />
                {!gesperrt && (
                  <button type="button" onClick={() => void grundrabatt()}>
                    Speichern
                  </button>
                )}
              </div>
            </Field>
            <Field label="Notiz">
              <span>{v.note ?? <span className="muted">–</span>}</span>
            </Field>
          </div>
          <h4>Abweichungen</h4>
          {v.deviations.length === 0 ? (
            <p className="muted">
              Keine Abweichungen: Der Grundrabatt gilt fuer alle Artikel.
            </p>
          ) : (
            <table>
              <tbody>
                {v.deviations.map((d) => (
                  <tr key={d.id}>
                    <td>{abweichungText(d)}</td>
                    <td className="num">
                      <strong>{pct(d.discountPercent)}</strong>
                    </td>
                    <td>
                      {admin && !gesperrt && (
                        <button
                          type="button"
                          className="link"
                          onClick={() => void entfernen(d.id)}
                        >
                          entfernen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {admin ? (
            !gesperrt && (
              <form onSubmit={abweichung} className="row" style={{ alignItems: 'end' }}>
                <Field label="Regelart">
                  <select
                    value={abw.dimension}
                    onChange={(e) =>
                      setAbw({
                        ...abw,
                        dimension: e.target.value as Dimension,
                        produktRef: '',
                        werkstoffRef: '',
                        artikelId: '',
                      })
                    }
                  >
                    <option value="PRODUCT_TYPE">Produkttyp</option>
                    <option value="MATERIAL">Werkstoff</option>
                    <option value="PRODUCT_MATERIAL">Produkttyp + Werkstoff</option>
                    <option value="ARTICLE">Einzelartikel</option>
                  </select>
                </Field>
                {(abw.dimension === 'PRODUCT_TYPE' ||
                  abw.dimension === 'PRODUCT_MATERIAL') && (
                  <Field label="Produkt">
                    <select
                      required
                      value={abw.produktRef}
                      onChange={(e) => setAbw({ ...abw, produktRef: e.target.value })}
                    >
                      <option value="">– waehlen –</option>
                      {(produkte.data?.items ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.typeValue.code} – {p.shortName}
                          {p.standardValue?.label?.de
                            ? ` ${p.standardValue.label.de}`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                {(abw.dimension === 'MATERIAL' ||
                  abw.dimension === 'PRODUCT_MATERIAL') && (
                  <Field label="Werkstoff">
                    <select
                      required
                      value={abw.werkstoffRef}
                      onChange={(e) => setAbw({ ...abw, werkstoffRef: e.target.value })}
                    >
                      <option value="">– waehlen –</option>
                      {(werkstoffe.data?.items ?? [])
                        .filter((m) => m.isActive)
                        .map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.code} – {m.label?.de ?? ''}
                          </option>
                        ))}
                    </select>
                  </Field>
                )}
                {abw.dimension === 'ARTICLE' && (
                  <Field label="Artikel" hint="Mindestens 2 Zeichen suchen.">
                    <ArtikelAuswahl
                      key={artikelSucheKey}
                      artikelId={abw.artikelId}
                      onSelect={(id) => setAbw({ ...abw, artikelId: id })}
                    />
                  </Field>
                )}
                <Field label="Rabatt in %">
                  <input
                    type="number"
                    required
                    min={0}
                    max={40}
                    step="0.5"
                    value={abw.prozent}
                    onChange={(e) => setAbw({ ...abw, prozent: e.target.value })}
                    style={{ width: '6rem' }}
                  />
                </Field>
                <button type="submit" className="primary">
                  Abweichung setzen
                </button>
              </form>
            )
          ) : (
            <p className="note">
              Abweichungen je Produkttyp, Werkstoff, Kombination oder Einzelartikel pflegt
              der Betreiber.
            </p>
          )}
        </>
      )}
    </section>
  );
}

interface RelevanteRegel {
  id: string;
  name: string;
  dimension: 'PRODUCT_TYPE' | 'MATERIAL';
  discountPercent: string;
  product: Deviation['product'];
  materialValue: Deviation['materialValue'];
  exempted: boolean;
}

/**
 * Ausnahmen von globalen Rabattregeln, ADR-0011. **Nur Betreiber.**
 *
 * Zeigt jede aktive oder kuenftig gueltige globale Regel mit Klartext-Bezug und
 * Status "gilt" oder "Konto ausgenommen" -- keine kryptische ID. Eine Ausnahme
 * verlangt eine Pflichtbegruendung; das Entfernen bestaetigt ausdruecklich, dass die
 * Regel danach fuer dieses Konto wieder greift.
 */
function AusnahmenVonGlobalenRegeln({
  accountId,
}: {
  accountId: string;
}): React.JSX.Element {
  const { data, error, reload } = useLoad<{ items: RelevanteRegel[] }>(
    `/api/admin/accounts/${accountId}/relevant-overrides`,
  );
  const [err, setErr] = useState<string | null>(null);
  const [neueAusnahme, setNeueAusnahme] = useState<{
    overrideId: string;
    name: string;
    reason: string;
  } | null>(null);
  const regeln = data?.items ?? [];

  async function anlegen(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (neueAusnahme === null) return;
    setErr(null);
    try {
      await api.post(`/api/admin/accounts/${accountId}/exemptions`, {
        overrideId: neueAusnahme.overrideId,
        reason: neueAusnahme.reason,
      });
      setNeueAusnahme(null);
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  async function entfernen(overrideId: string, name: string): Promise<void> {
    if (
      !window.confirm(
        `Ausnahme fuer "${name}" entfernen? Die globale Regel greift danach fuer ` +
          'dieses Konto ohne weitere Handlung wieder.',
      )
    )
      return;
    setErr(null);
    try {
      await api.post(`/api/admin/accounts/${accountId}/exemptions/${overrideId}/remove`);
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <section className="card">
      <h3>Ausnahmen von globalen Rabattregeln</h3>
      <Alert text={error} />
      <Alert text={err} />
      {regeln.length === 0 ? (
        <p className="muted">
          Zurzeit keine aktiven oder kuenftig gueltigen globalen Regeln.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Regel</th>
              <th>Bezug</th>
              <th className="num">Obergrenze</th>
              <th>Status</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {regeln.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>
                  {r.dimension === 'PRODUCT_TYPE'
                    ? r.product
                      ? `Produkttyp ${produktText(r.product)}`
                      : 'Produkttyp'
                    : r.materialValue
                      ? `Werkstoff ${werkstoffText(r.materialValue)}`
                      : 'Werkstoff'}
                </td>
                <td className="num">{pct(r.discountPercent)}</td>
                <td>
                  <StatusPill
                    active={!r.exempted}
                    activeText="gilt"
                    inactiveText="Konto ausgenommen"
                  />
                </td>
                <td>
                  {r.exempted ? (
                    <button
                      type="button"
                      className="link"
                      onClick={() => void entfernen(r.id, r.name)}
                    >
                      Ausnahme entfernen
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        setNeueAusnahme({ overrideId: r.id, name: r.name, reason: '' })
                      }
                    >
                      Ausnahme anlegen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {neueAusnahme && (
        <form onSubmit={anlegen} className="row" style={{ alignItems: 'end' }}>
          <Field label={`Begruendung fuer "${neueAusnahme.name}"`}>
            <input
              required
              value={neueAusnahme.reason}
              onChange={(e) =>
                setNeueAusnahme({ ...neueAusnahme, reason: e.target.value })
              }
              maxLength={500}
            />
          </Field>
          <button type="submit" className="primary">
            Ausnahme speichern
          </button>
          <button type="button" onClick={() => setNeueAusnahme(null)}>
            Abbrechen
          </button>
        </form>
      )}
    </section>
  );
}

/**
 * Artikelsuche fuer die Einzelartikelregel, ADR-0023.
 *
 * Textsuche verengt eine Auswahlliste -- keine UUID- oder Artikelnummerneingabe durch
 * den Administrator. Sucht ausschliesslich aktive Artikel; die Serverseite prueft das
 * beim Speichern zusaetzlich (`ConditionsService.pruefeArtikel`).
 */
function ArtikelAuswahl({
  artikelId,
  onSelect,
}: {
  artikelId: string;
  onSelect: (id: string) => void;
}): React.JSX.Element {
  const [suche, setSuche] = useState('');
  const [verzoegert, setVerzoegert] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setVerzoegert(suche.trim()), 300);
    return () => clearTimeout(timer);
  }, [suche]);
  const treffer = useLoad<{
    items: Array<{ id: string; articleNumber: string; description: string }>;
  }>(
    verzoegert.length >= 2
      ? `/api/admin/articles?q=${encodeURIComponent(verzoegert)}&active=true&limit=25`
      : null,
  );
  const items = treffer.data?.items ?? [];

  return (
    <>
      <input
        type="text"
        placeholder="Artikelnummer oder Bezeichnung"
        value={suche}
        onChange={(e) => {
          setSuche(e.target.value);
          onSelect('');
        }}
      />
      <select
        required
        value={artikelId}
        onChange={(e) => onSelect(e.target.value)}
        disabled={items.length === 0}
      >
        <option value="">
          {verzoegert.length < 2
            ? '– zuerst suchen –'
            : items.length === 0
              ? 'keine Treffer'
              : '– waehlen –'}
        </option>
        {items.map((a) => (
          <option key={a.id} value={a.id}>
            {a.articleNumber} – {a.description}
          </option>
        ))}
      </select>
    </>
  );
}
